import { yupResolver } from '@hookform/resolvers/yup';
import AdjustIcon from '@mui/icons-material/LibraryBooks';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import { Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';
import { v4 as uuidv4 } from 'uuid';
import { fetchJobBoardCandidate, searchCVLibrary, CVLibrarySearchResponse } from 'wisestep-api';
import { IFilterList, WsFilterTags, WsPagination } from 'wisestep-base';
import * as yup from 'yup';

import { PageHeader } from '../../../components/PageHeader';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import TalentPoolListSideNav from '../../candidates/talentpool/components/talentpoolListSideNav';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import basicSearchDefaultValues from '../utils/nativeSearchHelper';

import AdvanceFilter from './components/AdvanceFilters';
import ICVLibraryAdvanceSearch, { AdvancedSearchKeys } from './types/ICVLibraryAdvanceSearch';
import constructQuery from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import { pushSearchFilters } from './utils/pushSearchTags';

const CVLibrary = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const [cvLibrarySearchResponse, setCvLibrarySearchResponse] = useState<CVLibrarySearchResponse>();
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const methods = useForm<BasicSearchForm & ICVLibraryAdvanceSearch>({
    defaultValues: {
      ...basicSearchDefaultValues,
      ...defaultAdvanceFilterValues,
      lastActive: '1829',
      distance: 500,
    },
    resolver: yupResolver(
      yup.object().shape({ query: yup.string().required('Search query is required') })
    ),
  });
  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);
  const emptyAdvanceFieldValues = {
    ...defaultAdvanceFilterValues,
    ...{ excludeSocialOnlyProfiles: false, additionalFilters: [], contactMethod: [] },
  };
  const selectedIndex = (index: number, value: string) => console.log(index, value);

  const [openAdvanceFilter, setOpenAdvanceFilter] = useState(false);
  const page = methods.watch('page');
  const accountId = methods.watch('accountId');
  const performSearch = async (values: BasicSearchForm & ICVLibraryAdvanceSearch) => {
    setAppliedFilters(pushSearchFilters(values));
    setOpenAdvanceFilter(false);
    try {
      const response = await searchCVLibrary(constructQuery(values));
      setCvLibrarySearchResponse(response.data);
    } catch (e) {
      console.log('error', e);
    }
  };
  useEffect(() => {
    if (methods.formState.errors.query?.message) {
      setOpenAdvanceFilter(false);
      setNotification(failed('Search Query is Required'));
    }
  }, [methods.formState.errors.query, setNotification]);

  const onDeleteFilterTag = (tag: string) => {
    const idOfChip = appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, emptyAdvanceFieldValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = appliedFilters.findIndex((existChip) => existChip.id === idOfChip);
      appliedFilters.splice(index, 1);
      setAppliedFilters([...appliedFilters]);
    }
  };

  return (
    <>
      <PageHeader title="CV LIBRARY" />
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TalentPoolListSideNav selectedIndexValue={selectedIndex} />
        </Grid>
        <Grid item xs={9}>
          <FormProvider {...methods}>
            <NativeSearchForm
              jobBoardName="CV_LIBRARY"
              performSearch={performSearch}
              openAdvanceFilter={setOpenAdvanceFilter}
            />
            <AdvanceFilter
              performSearch={performSearch}
              open={openAdvanceFilter}
              onClose={() => setOpenAdvanceFilter(false)}
            />
            <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />

            {/* ========= Will work on this once get data ========== */}

            {cvLibrarySearchResponse &&
              cvLibrarySearchResponse.totalRecords &&
              Number(cvLibrarySearchResponse.totalRecords) > 0 && (
                <WsPagination
                  count={Math.ceil(Number(cvLibrarySearchResponse.totalRecords) / 50)}
                  onChange={(_, value) => {
                    methods.setValue('page', value + 1);
                    methods.handleSubmit(performSearch)();
                  }}
                  itemsPerPage={50}
                  pageNumber={page - 1}
                  defaultPage={page}
                  isPageCount={true}
                  type="Candidates"
                  totalCount={Number(cvLibrarySearchResponse.totalRecords)}
                />
              )}
          </FormProvider>

          {cvLibrarySearchResponse?.Result.map((candidate) => (
            <Grid my={2} key={candidate.Record_Id} item xs={12}>
              <CandidateCard
                candidateId={'' + candidate.Record_Id}
                title={candidate.Name}
                name={candidate.Name}
                imgSrc={''}
                isDownloaded={
                  cvLibrarySearchResponse.existing?.some(
                    (existingCandidate) =>
                      existingCandidate.candidateJobBoardId === candidate.Record_Id + ''
                  ) ?? false
                }
                skills={candidate.Skills ? candidate.Skills.split(',') : []}
                dateResumeLastUpdated={candidate.Last_Modified}
                onViewCvClick={async (candidateId) => {
                  try {
                    const candidateData = await fetchJobBoardCandidate({
                      jobBoardAccountId: accountId,
                      jobBoardId: candidateId,
                      force: false,
                      harvestingId: '',
                    });
                    const params: IQueryParam = {
                      resumeUpdatedOn: candidate.Last_Modified,
                      accountId: accountId,
                      jobBoardId: candidateId,
                      query: encodeURI(methods.getValues('query') ?? ''),
                      jobBoard: 'CVLibrary',
                    };
                    setCvViewSourcePage(ITrackCvViewJobBoards.CVLIBRARYSEARCH);
                    const paramId = `param${uuidv4()}`;
                    localStorage.setItem(paramId, getEncodedQueryParams(params));
                    openUrlInNewTab(
                      `/candidate-layout/profile?id=${
                        candidateData?.newCandidateData?.id
                      }&array=${[]}&q=${getEncodedQueryParams(params)}`
                    );
                  } catch (error) {
                    const err = error as AxiosError;
                    if (err.response?.data.status) {
                      setNotification(failed(err.response?.data.status));
                    } else {
                      setNotification(failed('There was some problem in fetching the candidate.'));
                    }
                  }
                }}
                detailItems={[
                  {
                    icon: <LocationOnIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                    label: `${candidate.Town}`,
                    tooltip: 'Candidate Location',
                  },
                  {
                    icon: <WorkIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                    label: `Job Title :${
                      candidate.DesiredJobTitle ? candidate.DesiredJobTitle : 'N/A'
                    }`,
                    tooltip: 'Current Job Title',
                  },
                  {
                    icon: <HiOutlineCurrencyDollar color="#888A8E" size="1.27rem" />,
                    label: candidate.ExpectedSalary
                      ? `Expected Salary:  ${candidate.ExpectedSalary}`
                      : 'Desired Salary: N/A',
                    tooltip: 'Desired Salary',
                  },
                  {
                    icon: <AdjustIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                    label: `Willing to Relocate: ${candidate.Relocate}`,
                    tooltip: 'Relocation Preference',
                  },
                ]}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </>
  );
};

export default CVLibrary;
